import type { CustomerFeatureKey, MenuItem } from '@busbook/shared';

/**
 * The fixed map from a menu item key to a route (docs/04 §4). Operators reorder, rename and
 * hide; they cannot invent pages, because there is no page behind a key that is not here.
 */
export const ROUTE_FOR: Record<string, string> = {
  search: '/',
  bookings: '/bookings',
  travellers: '/travellers',
  profile: '/profile',
  offers: '/offers',
  help: '/help',
  about: '/about',
};

/**
 * Menu entries that only exist when the operator has the matching feature switched on.
 *
 * A tab is a promise that the page behind it works. Travellers behind a disabled feature
 * is a screen that tells you to go away, which is worse than not offering it.
 */
const FEATURE_FOR: Partial<Record<string, CustomerFeatureKey>> = {
  travellers: 'travellers',
};

export function visibleItems(
  items: MenuItem[],
  enabled: (key: CustomerFeatureKey) => boolean,
): MenuItem[] {
  return items.filter((item) => {
    if (!item.visible || !ROUTE_FOR[item.key]) return false;
    const feature = FEATURE_FOR[item.key];
    return feature ? enabled(feature) : true;
  });
}

/**
 * Which item the current path is on, or -1.
 *
 * Longest match wins so `/bookings/AB12CD/dispute` still lights the trips tab, while `/`
 * only ever matches itself — every path starts with a slash, so a prefix test would light
 * search on every screen in the app.
 */
export function activeIndex(items: MenuItem[], pathname: string): number {
  let best = -1;
  let bestLength = -1;
  items.forEach((item, index) => {
    const route = ROUTE_FOR[item.key];
    if (!route) return;
    const matches = route === '/' ? pathname === '/' : pathname === route || pathname.startsWith(`${route}/`);
    if (matches && route.length > bestLength) {
      best = index;
      bestLength = route.length;
    }
  });
  return best;
}
